import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, decimalStringSchema, isoDateSchema, isoDateTimeSchema, localeSchema, uuidSchema, } from './common.js';
/**
 * Prices (docs/partial/shopping-in-store-spec.md 6.3–6.8): what a product – or an ingredient
 * sold loose – cost at a store on a day. Household data; money is a decimal string with two
 * places and every price carries its own currency. Nothing is converted, nothing is added
 * across currencies.
 */
export const moneySchema = z
    .string()
    .regex(/^\d{1,6}(\.\d{1,2})?$/, 'Expected an amount such as "1.19"');
/** Signed: differences and discounts. */
export const signedMoneySchema = z.string().regex(/^-?\d{1,7}(\.\d{1,2})?$/);
export const currencyCodeSchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'Expected an ISO 4217 code such as "EUR"');
/** Offered in the profile; any other ISO code can be typed. */
export const COMMON_CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP', 'PLN', 'CZK', 'DKK', 'SEK'];
export const priceSourceSchema = z.enum(['MANUAL', 'PRICE_TAG', 'RECEIPT']);
export const unitPriceBaseSchema = z.enum(['KG', 'L', 'PIECE']);
export const matchConfidenceSchema = z.enum(['EXACT', 'LIKELY', 'POSSIBLE']);
export const priceObservationSchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    productId: uuidSchema.nullable(),
    ingredientId: uuidSchema.nullable(),
    /** Where it was seen (a branch or a chain); null = "somewhere", never part of a total. */
    storeId: uuidSchema.nullable(),
    /** The chain that answers as fallback: the branch's parent, or the store itself. */
    chainId: uuidSchema.nullable(),
    /** For one sales unit; a deposit is not part of it. */
    price: moneySchema,
    currency: currencyCodeSchema,
    /** Loose goods: "2.99 per 1 kg". */
    perQuantity: decimalStringSchema.nullable(),
    perUnitCode: z.string().nullable(),
    /** The product's package on that day. */
    packageQuantity: decimalStringSchema.nullable(),
    packageUnitCode: z.string().nullable(),
    unitPrice: decimalStringSchema.nullable(),
    unitPriceBase: unitPriceBaseSchema.nullable(),
    isPromotion: z.boolean(),
    promotionEndsAt: isoDateSchema.nullable(),
    depositAmount: moneySchema.nullable(),
    source: priceSourceSchema,
    receiptId: uuidSchema.nullable(),
    observedAt: isoDateSchema,
    note: z.string().nullable(),
    createdById: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    /** The reader may change or delete it (author, or an admin of the household). */
    editable: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
const priceFields = {
    storeId: uuidSchema.nullable().optional(),
    price: moneySchema,
    /** Omitted: the writer's currency (profile, else the instance default). */
    currency: currencyCodeSchema.optional(),
    perQuantity: decimalStringSchema.nullable().optional(),
    perUnitCode: z.string().max(32).nullable().optional(),
    isPromotion: z.boolean().optional(),
    promotionEndsAt: isoDateSchema.nullable().optional(),
    depositAmount: moneySchema.nullable().optional(),
    /** Omitted: today. Never in the future. */
    observedAt: isoDateSchema.optional(),
    note: z.string().trim().max(300).nullable().optional(),
};
export const priceWriteSchema = z
    .object({
    /** Client-generated id: a price typed offline is created exactly once. */
    id: uuidSchema.optional(),
    /** null = only me; omitted = the first household the writer may edit. */
    householdId: uuidSchema.nullable().optional(),
    productId: uuidSchema.nullable().optional(),
    ingredientId: uuidSchema.nullable().optional(),
    ...priceFields,
    source: z.enum(['MANUAL', 'PRICE_TAG']).default('MANUAL'),
    /** The price tag scan this price confirms; it leaves the inbox. */
    tagScanId: uuidSchema.optional(),
})
    .refine((v) => Boolean(v.productId) !== Boolean(v.ingredientId), {
    message: 'Give either productId or ingredientId',
    path: ['productId'],
});
export const priceUpdateSchema = z
    .object(priceFields)
    .partial()
    .extend({ version: z.number().int().nonnegative() });
export const priceListQuerySchema = cursorQuerySchema.extend({
    productId: uuidSchema.optional(),
    ingredientId: uuidSchema.optional(),
    storeId: uuidSchema.optional(),
    chainId: uuidSchema.optional(),
    /** Only the latest price per store. */
    current: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const priceObservationPageSchema = cursorPageSchema(priceObservationSchema);
// ---------------------------------------------------------------------------
// Current prices and changes
// ---------------------------------------------------------------------------
export const packageChangeSchema = z.object({
    from: z.object({ quantity: decimalStringSchema, unitCode: z.string() }),
    to: z.object({ quantity: decimalStringSchema, unitCode: z.string() }),
});
export const priceChangeSchema = z.object({
    productId: uuidSchema.nullable(),
    storeId: uuidSchema.nullable(),
    chainId: uuidSchema.nullable(),
    currency: currencyCodeSchema,
    /** PRICE: the two prices; UNIT_PRICE: the package changed, the unit prices are compared. */
    basis: z.enum(['PRICE', 'UNIT_PRICE']),
    current: z.object({ price: moneySchema, observedAt: isoDateSchema }),
    previous: z.object({ price: moneySchema, observedAt: isoDateSchema }),
    absolute: signedMoneySchema,
    /** Signed; whole numbers from 10 %, one decimal below. */
    percent: z.string(),
    direction: z.enum(['UP', 'DOWN']),
    /** Large enough for an arrow (spec 6.8). */
    noticeable: z.boolean(),
    since: isoDateSchema,
    packageChange: packageChangeSchema.nullable(),
});
export const currentPriceSchema = z.object({
    observation: priceObservationSchema,
    storeName: z.string().nullable(),
    /** Older than PRICE_STALE_DAYS: shown as "ca.", never the cheapest. */
    stale: z.boolean(),
    change: priceChangeSchema.nullable(),
});
/** Added to a product for its reader: latest price per store and the cheapest in the reader's currency. */
export const productPricesSchema = z.object({
    currency: currencyCodeSchema,
    currentPrices: z.array(currentPriceSchema),
    cheapest: currentPriceSchema.nullable(),
});
export const priceHistoryQuerySchema = z.object({
    storeId: uuidSchema.optional(),
    chainId: uuidSchema.optional(),
    months: z.coerce.number().int().min(1).max(60).default(12),
});
export const priceHistorySchema = z.object({
    productId: uuidSchema,
    currency: currencyCodeSchema,
    observations: z.array(priceObservationSchema),
    change: priceChangeSchema.nullable(),
    trend: z
        .object({
        first: z.object({ price: moneySchema, observedAt: isoDateSchema }),
        last: z.object({ price: moneySchema, observedAt: isoDateSchema }),
        absolute: signedMoneySchema,
        percent: z.string(),
    })
        .nullable(),
});
export const priceChangesQuerySchema = cursorQuerySchema.extend({
    storeId: uuidSchema.optional(),
    chainId: uuidSchema.optional(),
    since: isoDateSchema.optional(),
});
export const priceChangeEntrySchema = priceChangeSchema.extend({
    productName: z.string().nullable(),
    storeName: z.string().nullable(),
});
export const priceChangePageSchema = cursorPageSchema(priceChangeEntrySchema);
export const storePriceIndexQuerySchema = z.object({
    months: z.coerce
        .number()
        .int()
        .refine((v) => v === 3 || v === 6 || v === 12, 'Expected 3, 6 or 12')
        .default(6),
});
export const storePriceIndexSchema = z.object({
    storeId: uuidSchema,
    months: z.number().int(),
    currency: currencyCodeSchema,
    /** Null: fewer comparable products than PRICE_INDEX_MIN_PRODUCTS – "not enough data yet". */
    index: z
        .object({
        percent: z.string(),
        products: z.number().int(),
        up: z.number().int(),
        down: z.number().int(),
        unchanged: z.number().int(),
    })
        .nullable(),
    comparableProducts: z.number().int(),
    minProducts: z.number().int(),
    top: z.array(priceChangeEntrySchema),
});
// ---------------------------------------------------------------------------
// Estimate of a shopping list (spec 6.7)
// ---------------------------------------------------------------------------
export const estimateLineSchema = z.object({
    itemId: uuidSchema,
    itemName: z.string(),
    amount: moneySchema,
    multiplier: z.string(),
    via: z.enum(['ITEM_PRODUCT', 'LEARNED', 'INGREDIENT_PRODUCT', 'INGREDIENT']),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    price: moneySchema,
    observedAt: isoDateSchema,
    stale: z.boolean(),
    viaChain: z.boolean(),
    isPromotion: z.boolean(),
    promotionEndsAt: isoDateSchema.nullable(),
});
export const listEstimateSchema = z.object({
    storeId: uuidSchema,
    storeName: z.string(),
    total: moneySchema,
    currency: currencyCodeSchema,
    pricedItems: z.number().int(),
    openItems: z.number().int(),
    /** At least half of the open items have a price: the total may be shown. */
    enough: z.boolean(),
    approximate: z.boolean(),
    lines: z.array(estimateLineSchema),
    missing: z.array(z.object({ itemId: uuidSchema, itemName: z.string() })),
});
export const listEstimateQuerySchema = z.object({ storeId: uuidSchema });
export const listEstimateCompareSchema = z.object({
    currency: currencyCodeSchema,
    stores: z.array(listEstimateSchema),
    commonBasket: z
        .object({
        itemIds: z.array(uuidSchema),
        totals: z.array(z.object({ storeId: uuidSchema, total: moneySchema })),
        /** Null on a tie. Only the common basket names a cheapest store. */
        cheapestStoreId: uuidSchema.nullable(),
    })
        .nullable(),
});
// ---------------------------------------------------------------------------
// Price tag scan (spec 6.4)
// ---------------------------------------------------------------------------
export const priceTagScanRequestSchema = z.object({
    /** Client-generated: a photo queued offline is processed exactly once. */
    id: uuidSchema.optional(),
    assetId: uuidSchema,
    /** Started from a product: the tag's product is known. */
    productId: uuidSchema.nullable().optional(),
    storeId: uuidSchema.nullable().optional(),
    language: localeSchema.optional(),
});
/** Input stored on the AIGeneration row for PRICE_TAG_EXTRACTION. */
export const aiPriceTagInputSchema = z.object({
    scanId: uuidSchema,
    language: localeSchema.default('de'),
});
/** What the photo says – a draft that prefills the form. Nothing is saved from it. */
export const priceTagDraftSchema = z.object({
    productName: z.string().nullable(),
    brand: z.string().nullable(),
    price: moneySchema,
    currency: currencyCodeSchema.nullable(),
    packageQuantity: decimalStringSchema.nullable(),
    packageUnitCode: z.string().nullable(),
    packageUnitText: z.string().nullable(),
    unitPrice: decimalStringSchema.nullable(),
    unitPriceBase: unitPriceBaseSchema.nullable(),
    /** The printed unit price contradicts price ÷ package size. */
    unitPriceMismatch: z.boolean(),
    isPromotion: z.boolean(),
    promotionEndsAt: isoDateSchema.nullable(),
    /** Printed on the tag and valid; null otherwise. */
    barcode: z.string().nullable(),
    /** The household's or the global product with that barcode, when there is one. */
    matchedProductId: uuidSchema.nullable(),
    confidence: z.enum(['HIGH', 'MEDIUM', 'LOW']),
});
export const priceTagScanStatusSchema = z.enum([
    'PROCESSING',
    'READY',
    'FAILED',
    'DONE',
    'DISMISSED',
]);
export const priceTagScanSchema = z.object({
    id: uuidSchema,
    status: priceTagScanStatusSchema,
    generationId: uuidSchema.nullable(),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    storeId: uuidSchema.nullable(),
    /** WORKER_ERROR_CODES entry when the photo could not be read. */
    errorCode: z.string().nullable(),
    draft: priceTagDraftSchema.nullable(),
    photoUrl: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const priceInboxSchema = z.object({ items: z.array(priceTagScanSchema) });
//# sourceMappingURL=prices.js.map